'use client'

import { Spacer } from '@vinted/web-ui'
import { useContext } from 'react'

import { useSession } from '@marketplace-web/shared/session-data'
import {
  EscrowFeesContext,
  ShippingDetailsContext,
} from '@marketplace-web/item-page/item-page-data'

import { transformCurrencyAmountDto } from '@marketplace-web/currency/currency-data'
import { useAbTest } from '@marketplace-web/feature-flags/ab-tests-data'

import ServiceFeeIncludedTitle from '../ServiceFeeIncludedTitle'
import DiscountBadges from './DiscountBadges'
import ItemCurrencyConversionNote from './ItemCurrencyConversionNote'
import ItemPriceHeading from './ItemPriceHeading'
import { ItemPagePricingPluginModel, ItemPhotoDto, ItemThumbnailSize } from './types'

type Props = {
  data: ItemPagePricingPluginModel
}

const ItemPagePricingPlugin = ({ data }: Props) => {
  const { user } = useSession()
  const currentUserId = user?.id
  const isUserLoggedIn = !!user
  const { shippingDetails } = useContext(ShippingDetailsContext)
  const { escrowFees } = useContext(EscrowFeesContext)
  const {
    itemPrice,
    offerPrice,
    totalItemPrice,
    serviceFee,
    itemId,
    isSellerBusiness,
    itemUserId,
    itemTitle,
    photos,
    conversion,
  } = data
  const unifiedPriceBreakdownIsAbTestIsOn =
    useAbTest('unified_price_breakdown_v2')?.variant === 'on'

  const isViewingSelf = itemUserId === currentUserId

  const transformedItemPrice = transformCurrencyAmountDto(itemPrice)
  const transformedTotalPrice = totalItemPrice ? transformCurrencyAmountDto(totalItemPrice) : null
  const transformedServiceFee = serviceFee ? transformCurrencyAmountDto(serviceFee) : null

  const getPhotoThumbnail = (photo: ItemPhotoDto) => {
    const thumbnail = photo?.thumbnails?.find(thumb => thumb.type === ItemThumbnailSize.Medium)

    return thumbnail?.url
  }

  const itemPhotoSrc = photos && getPhotoThumbnail(photos[0]!)

  const shouldShowDetailedBreakdown =
    (isUserLoggedIn || unifiedPriceBreakdownIsAbTestIsOn) && !!escrowFees

  const detailedBreakdownProps = shouldShowDetailedBreakdown
    ? {
        itemTitle,
        escrowFees,
        itemPhotoSrc,
      }
    : null

  const shouldShowDiscountBadges = isUserLoggedIn && !isViewingSelf && !!escrowFees

  return (
    <div className="details-list details-list--pricing">
      <div data-testid="item-sidebar-price-container">
        <ItemPriceHeading
          price={transformedItemPrice}
          offerPrice={offerPrice}
          isItemOwner={isViewingSelf}
        />
        {!!shippingDetails?.price && (
          <ServiceFeeIncludedTitle
            itemId={itemId}
            itemPrice={offerPrice || transformedItemPrice}
            isItemOwner={isViewingSelf}
            totalItemPrice={transformedTotalPrice}
            serviceFee={transformedServiceFee}
            isSellerBusiness={isSellerBusiness}
            shouldTrimTitle={isViewingSelf}
            detailedBreakdown={detailedBreakdownProps}
            showInclinePrice={false}
            trackUnification={!isUserLoggedIn}
          />
        )}
        {shouldShowDiscountBadges && (
          <DiscountBadges
            escrowFees={escrowFees}
            isSellerBusiness={isSellerBusiness}
            itemUserId={itemUserId}
            itemTitle={itemTitle}
            itemPhotoSrc={itemPhotoSrc}
            itemPrice={transformedItemPrice}
            itemId={itemId}
          />
        )}
      </div>
      <div
        className="details-list__item details-list--badges"
        data-testid="item-sidebar-pricing-details-container"
      >
        {conversion && (
          <>
            <Spacer />
            <ItemCurrencyConversionNote />
          </>
        )}
      </div>
    </div>
  )
}

export default ItemPagePricingPlugin
